import type { CarrierRequest, CarrierWorkspace } from "@agency/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { DemoWorkflowCard, RequestStatusBadge } from "../screens/Carriers";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";
import { Badge, Button, Card, CardHeader, EmptyState } from "./ui";

/** Customer page → Carrier tab: this customer's carrier requests (simulated ones labeled) and a shortcut to start one. */
export function CarrierPanel({ customerId }: { customerId: string }) {
  const t = useT();
  const { locale } = useLocale();
  const [starting, setStarting] = useState(false);
  const reqs = useQuery({ queryKey: ["carrier-requests", customerId], queryFn: async () => (await callAction<"agency.carrier.request.list", Array<CarrierRequest & { customerName: string }>>("agency.carrier.request.list", { customerId, limit: 50 })).result });
  const ws = useQuery({ queryKey: ["carriers"], queryFn: async () => (await callAction<"agency.carrier.list", CarrierWorkspace>("agency.carrier.list", {})).result, enabled: starting });
  const list = reqs.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button variant={starting ? "outline" : "primary"} onClick={() => setStarting((v) => !v)}>{starting ? t("common.close") : t("carriers.panel.new")}</Button></div>
      {starting && ws.data ? <DemoWorkflowCard carriers={ws.data.carriers} initialCustomerId={customerId} compact /> : null}
      {list.length === 0 ? <EmptyState title={t("carriers.panel.none")} /> : (
        <Card>
          <CardHeader title={t("carriers.panel.title")} />
          <ul className="divide-y divide-ink/5">
            {list.map((r) => (
              <li key={r.id} className="px-5 py-2 flex items-center gap-2 text-sm">
                <div className="flex-1 min-w-0">
                  <Link to={`/carriers/requests/${r.id}`} className="font-semibold hover:underline">{t(`carriers.kind.${r.kind}`)} · {r.carrierName}</Link>
                  <p className="text-xs text-ink/55">{shortDate(r.updatedAt, locale)}{r.questions.some((q) => !q.answeredAt) ? ` · ${t("carriers.status.needs_info")}` : ""}</p>
                </div>
                {r.simulated ? <Badge tone="amber">{t("carriers.simulated")}</Badge> : null}
                <RequestStatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
